import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, Copy, KeyRound, RotateCcw } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Alert, ICON, TextField } from "../components/ui";
import { Filter, Loadable, PageHead, post, type Row, SearchBox, Table, text, useAdmin, when } from "./ui";

// Conturi și suport: users (disable, new password link), the site's contact inbox, invitations and the audit log.

/** A one-time link (invitation or new password): shown once, copied, never stored in clear. */
function OneTimeLink({ email, link, expiresAt }: { email: string; link: string; expiresAt: number }) {
  const [copied, setCopied] = useState(false);
  return (
    <section className="card stack" style={{ gap: 8, maxWidth: 720 }} aria-live="polite">
      <Alert tone="warning" title={`Link pentru ${email}: afișat o singură dată`}>
        Valabil până la {when(expiresAt)}. Trimite-l pe un canal privat, nu în documente publice.
      </Alert>
      <code style={{ overflowWrap: "anywhere" }}>{link}</code>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        style={{ alignSelf: "flex-start" }}
        onClick={() => void navigator.clipboard.writeText(link).then(() => setCopied(true))}
      >
        <Copy {...ICON} aria-hidden="true" />
        {copied ? "Copiat" : "Copiază linkul"}
      </button>
    </section>
  );
}

export function Users() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const qc = useQueryClient();
  const list = useAdmin<{ users: Row[] }>(["users", q], `/users?q=${encodeURIComponent(q)}`);
  const [link, setLink] = useState<{ email: string; link: string; expiresAt: number } | null>(null);
  const toggle = useMutation({
    mutationFn: ({ id, disable }: { id: string; disable: boolean }) =>
      post(`/users/${id}/${disable ? "disable" : "enable"}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
  const reset = useMutation({
    mutationFn: (u: Row) =>
      post<{ link: string; expiresAt: number }>("/invites", {
        email: u.email,
        name: u.name,
        superadmin: false,
      }),
    onSuccess: (r, u) => setLink({ email: String(u.email), ...r }),
  });
  return (
    <>
      <PageHead title="Utilizatori" sub="Conturile restaurantelor și ale echipei Meniova." />
      <SearchBox
        value={q}
        onChange={(v) => setParams(v ? { q: v } : {}, { replace: true })}
        label="Caută după e-mail sau nume"
      />
      {(toggle.isError || reset.isError) && (
        <Alert tone="error" live>
          {((toggle.error ?? reset.error) as Error).message}
        </Alert>
      )}
      {link && <OneTimeLink {...link} />}
      <Loadable q={list}>
        {(d) => (
          <Table
            head={["E-mail", "Nume", "Restaurante", "Stare", "Ultima intrare", ""]}
            rows={d.users.map((u) => [
              <span key="e">
                {text(u.email)} {u.is_superadmin ? <span className="chip chip-info">admin</span> : null}
              </span>,
              text(u.name),
              text(u.restaurants),
              u.disabled_at
                ? "dezactivat"
                : u.invite_pending
                  ? "invitație nefolosită"
                  : u.email_verified_at
                    ? "activ"
                    : "e-mail neconfirmat",
              when(u.last_session),
              <span key="a" className="row" style={{ gap: 4, flexWrap: "wrap" }}>
                {!u.is_superadmin && !u.disabled_at && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={reset.isPending}
                    title="Link de unică folosință prin care își alege o parolă nouă"
                    onClick={() => reset.mutate(u)}
                  >
                    <KeyRound {...ICON} aria-hidden="true" /> Link parolă nouă
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: String(u.id), disable: !u.disabled_at })}
                >
                  {u.disabled_at ? (
                    <>
                      <RotateCcw {...ICON} aria-hidden="true" /> Reactivează
                    </>
                  ) : (
                    <>
                      <Ban {...ICON} aria-hidden="true" /> Dezactivează
                    </>
                  )}
                </button>
              </span>,
            ])}
          />
        )}
      </Loadable>
    </>
  );
}

/** Messages sent from the contact form of meniova.com: read, answer by e-mail, mark handled. */
export function Support() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "new";
  const q = useAdmin<{ messages: Row[]; open: number }>(["support", status], `/support?status=${status}`);
  const handled = useMutation({
    mutationFn: (id: string) => post(`/support/${id}/handled`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  return (
    <>
      <PageHead title="Mesaje suport" sub="Trimise din formularul de contact de pe meniova.com." />
      <Filter
        label="Afișează"
        value={status}
        onChange={(v) => setParams({ status: v }, { replace: true })}
        options={[
          ["new", "Fără răspuns"],
          ["handled", "Rezolvate"],
          ["all", "Toate"],
        ]}
      />
      <Loadable q={q}>
        {(d) =>
          d.messages.length ? (
            <ul className="verify-list">
              {d.messages.map((m) => (
                <li key={String(m.id)} className="verify-row">
                  <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {text(m.name)} · {text(m.email)}
                    </strong>
                    <span className="caption muted">
                      {when(m.created_at)}
                      {m.restaurant ? ` · ${String(m.restaurant)}` : ""}
                    </span>
                    <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {text(m.message)}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <a
                      className="btn btn-secondary btn-sm"
                      href={`mailto:${String(m.email)}?subject=${encodeURIComponent("Re: mesajul tău către Meniova")}`}
                    >
                      Răspunde pe e-mail
                    </a>
                    <Link
                      className="btn btn-ghost btn-sm"
                      to={`/admin/restaurante?q=${encodeURIComponent(String(m.email))}`}
                    >
                      Caută restaurantul
                    </Link>
                    {m.status === "new" && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={handled.isPending}
                        onClick={() => handled.mutate(String(m.id))}
                      >
                        <Check {...ICON} aria-hidden="true" />
                        Rezolvat
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Niciun mesaj.</p>
          )
        }
      </Loadable>
    </>
  );
}

export function Audit() {
  const q = useAdmin<{ events: Row[] }>(["audit"], "/audit");
  return (
    <>
      <PageHead
        title="Jurnal (audit)"
        sub="Ultimele 200 de acțiuni importante, ale restaurantelor și ale echipei."
      />
      <Loadable q={q}>
        {(d) => (
          <Table
            head={["Când", "Acțiune", "Cine", "Obiect"]}
            rows={d.events.map((e) => [
              when(e.created_at),
              text(e.action),
              e.actor_type === "support"
                ? `echipa (${text(e.actor_email)})`
                : text(e.actor_email ?? e.actor_type),
              e.restaurant_id ? (
                <Link key="r" to={`/admin/restaurante/${String(e.restaurant_id)}`}>
                  restaurant
                </Link>
              ) : (
                text(e.entity_type)
              ),
            ])}
          />
        )}
      </Loadable>
    </>
  );
}

export function Invites() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [superadmin, setSuperadmin] = useState(false);
  const [result, setResult] = useState<{ link: string; expiresAt: number; email: string } | null>(null);
  const invite = useMutation({
    mutationFn: () => post<{ link: string; expiresAt: number }>("/invites", { email, name, superadmin }),
    onSuccess: (r) => {
      setResult({ ...r, email });
      setEmail("");
      setName("");
    },
  });
  return (
    <>
      <PageHead
        title="Invitații"
        sub="Creezi un cont pentru cineva (un client ajutat de echipă sau un coleg Meniova). Persoana își alege parola din link."
      />
      <form
        className="card stack"
        style={{ gap: 12, maxWidth: 520 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        <TextField id="inv-email" label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField id="inv-name" label="Nume" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="check-row">
          <input type="checkbox" checked={superadmin} onChange={(e) => setSuperadmin(e.target.checked)} />
          <span>Administrator Meniova (acces la admin.meniova.com)</span>
        </label>
        {invite.isError && <span className="field-error">{(invite.error as Error).message}</span>}
        <button type="submit" className="btn btn-primary" disabled={invite.isPending || !email || !name}>
          Creează invitația
        </button>
      </form>
      {result && <OneTimeLink {...result} />}
    </>
  );
}
